import { useContext } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { LanguageContext } from "../App";
import { IMAGES } from "../translations";

export default function Hero() {
  const { t } = useContext(LanguageContext);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative w-full h-screen min-h-[600px] flex items-center justify-center overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center hero-bg"
        style={{ backgroundImage: `url(${IMAGES.hero})`, backgroundPosition: "center 40%" }}
        data-testid="hero-bg"
      />
      <div className="absolute inset-0 bg-black/40" />

      <div className="relative z-10 text-center px-5 max-w-4xl mx-auto w-full">

        {/* Overline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.3em] text-white/60 mb-5"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        {/* Main headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="font-['Cormorant_Garamond',serif] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-white tracking-tight leading-tight whitespace-pre-line mb-4"
          data-testid="hero-headline"
        >
          {t.hero.headline}
        </motion.h1>

        {/* Evocative subheadline — place identity */}
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="font-['Cormorant_Garamond',serif] text-xl md:text-2xl font-light italic text-white/80 mb-2"
          data-testid="hero-subheadline"
        >
          {t.hero.subheadline}
        </motion.p>

        {/* Manifesto tagline */}
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="font-['Manrope',sans-serif] text-[11px] uppercase tracking-[0.35em] text-white/40 mb-10"
          data-testid="hero-manifesto"
        >
          {t.hero.manifesto}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 px-4 sm:px-0"
        >
          <Link
            to="/prenota"
            data-testid="hero-book-btn"
            className="bg-white text-[#2A2726] font-['Manrope',sans-serif] text-xs uppercase tracking-[0.25em] px-8 py-4 hover:bg-[#F5F1EA] transition-all duration-300 text-center w-full sm:w-auto sm:min-w-[220px]"
          >
            {t.hero.cta}
          </Link>
          <Link
            to="/camere"
            data-testid="hero-rooms-btn"
            className="border border-white/60 text-white font-['Manrope',sans-serif] text-xs uppercase tracking-[0.25em] px-8 py-4 hover:border-white hover:bg-white/10 transition-all duration-300 text-center w-full sm:w-auto sm:min-w-[220px]"
          >
            {t.hero.cta2}
          </Link>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        data-testid="hero-scroll-btn"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40"
      >
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}>
          <ChevronDown size={18} />
        </motion.div>
      </motion.div>
    </section>
  );
}
